import * as React from "react";
import { ArrowUp, CircleAlert, CircleStop, FileText, Image as ImageIcon, Paperclip } from "lucide-react";

import { FOCUS_RING_FRAME_PEER } from "./lib/focus";
import { cn } from "./lib/utils";
import { Chip, ChipGroup } from "./ui/chip";
import { IconButton } from "./ui/icon-button";
import { Textarea } from "./ui/textarea";

/**
 * v2 ChatComposer (redesign phase 5, #455; break #137): one composer for the
 * web app's chat page (`md`) and the Assistant sheet / add-in drawer (`sm`),
 * replacing AssistantChatInput's markup. Controlled and presentational: draft
 * persistence, `restore`, uploads (useChatAttachments), naming pasted
 * screenshots and the research mode stay in the app (04, "Chat").
 *
 * Structure: root > [attachment chips] textarea [actions row] frame. As in
 * Input, the frame is an empty span AFTER the textarea that draws border and
 * focus ring from the textarea's own state (`peer-*`), so focusing the send
 * button doesn't light up the whole composer. Clicks on the padding focus
 * the textarea.
 *
 * Keys: Enter sends, Shift+Enter is a new line (as v1); Enter during an IME
 * composition never sends. Send is the one blue action, disabled while there
 * is nothing to send (no text and no uploaded file), while a file uploads,
 * or while an answer is generated. With `onStop`, "Antwort stoppen" replaces
 * send while generating; if it had focus when it swaps back, focus moves to
 * the textarea (and from a focused send to stop).
 *
 * No "Nachricht gesendet" status: the message appears in the thread and the
 * field clears; the thread announces the answer.
 */

export type ChatComposerSize = "md" | "sm";

export interface ChatComposerAttachment {
  id: string;
  name: string;
  status: "uploading" | "ready" | "failed";
  /** 0–100 while uploading. */
  progress?: number;
  kind?: "image" | "file";
}

export interface ChatComposerLabels {
  /** The textarea's name ("Nachricht an lytra"); the placeholder is not a label. */
  input: string;
  placeholder?: string;
  send: string;
  stop: string;
  attach: string;
  /** Name of an attachment's remove button: "<name> entfernen". */
  remove: (name: string) => string;
  /** The attachment list's name ("Anhänge"). */
  attachments: string;
  /** Shown while files are dragged over the composer. */
  dropHint: string;
  /** Label suffix while uploading: "45 %". */
  uploading: (progress: number) => string;
  /** Label suffix of a failed upload: "fehlgeschlagen". */
  failed: string;
  /** Counter text with `maxLength`; default "n / max". */
  counter?: (length: number, max: number) => string;
}

export interface ChatComposerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onSubmit" | "children"> {
  size?: ChatComposerSize;
  value: string;
  onValueChange: (value: string) => void;
  /** Enter or Senden, only when there is something to send. The app reads `value` and clears it. */
  onSubmit: () => void;
  /** The send request is in flight: send shows the spinner. */
  sending?: boolean;
  /** An answer is being generated. */
  generating?: boolean;
  /** Stops the generation; without it, send shows the spinner while generating. */
  onStop?: () => void;
  disabled?: boolean;
  attachments?: readonly ChatComposerAttachment[];
  onRemoveAttachment?: (id: string) => void;
  /** Adds the attach button and accepts pasted and dropped files. */
  onAddFiles?: (files: File[]) => void;
  /** The file picker's `accept`. */
  accept?: string;
  /** Right of the actions row, before send (the research-mode menu). */
  toolbar?: React.ReactNode;
  hint?: React.ReactNode;
  maxLength?: number;
  inputRef?: React.Ref<HTMLTextAreaElement>;
  labels: ChatComposerLabels;
}

const SIZE: Record<ChatComposerSize, { root: string; button: "md" | "sm"; maxRows: number }> = {
  md: { root: "gap-2 p-3", button: "md", maxRows: 8 },
  sm: { root: "gap-2 p-2", button: "sm", maxRows: 5 },
};

function hasFiles(event: React.DragEvent) {
  return Array.from(event.dataTransfer.types).includes("Files");
}

export const ChatComposer = React.forwardRef<HTMLDivElement, ChatComposerProps>(function ChatComposer(
  {
    size = "md",
    value,
    onValueChange,
    onSubmit,
    sending = false,
    generating = false,
    onStop,
    disabled = false,
    attachments = [],
    onRemoveAttachment,
    onAddFiles,
    accept,
    toolbar,
    hint,
    maxLength,
    inputRef,
    labels,
    className,
    onMouseDown,
    ...props
  },
  ref,
) {
  const s = SIZE[size];
  const hintId = React.useId();
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);
  const sendRef = React.useRef<HTMLButtonElement>(null);
  const stopRef = React.useRef<HTMLButtonElement>(null);
  const [dragActive, setDragActive] = React.useState(false);

  const setTextarea = React.useCallback(
    (el: HTMLTextAreaElement | null) => {
      textareaRef.current = el;
      if (typeof inputRef === "function") inputRef(el);
      else if (inputRef) (inputRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
    },
    [inputRef],
  );

  const uploading = attachments.some((a) => a.status === "uploading");
  const hasReadyFile = attachments.some((a) => a.status === "ready");
  const showStop = generating && onStop != null;
  const canSend = !disabled && !sending && !generating && !uploading && (value.trim() !== "" || hasReadyFile);

  const submit = () => {
    if (canSend) onSubmit();
  };

  // Send and stop replace each other: a focused one hands focus on, so it is never lost.
  const focusedAction = React.useRef<"send" | "stop" | null>(null);
  React.useLayoutEffect(() => {
    if (showStop && focusedAction.current === "send") stopRef.current?.focus();
    if (!showStop && focusedAction.current === "stop") textareaRef.current?.focus();
    focusedAction.current = null;
  }, [showStop]);

  // The last chip's remove would leave focus on nothing (the list goes): back to the textarea.
  const focusAfterLastRemove = React.useRef(false);
  React.useEffect(() => {
    if (attachments.length === 0 && focusAfterLastRemove.current) textareaRef.current?.focus();
    focusAfterLastRemove.current = false;
  }, [attachments.length]);

  const describedBy = hint != null || maxLength != null ? hintId : undefined;

  return (
    <div
      ref={ref}
      data-slot="chat-composer"
      data-size={size}
      data-drag-active={dragActive ? "" : undefined}
      className={cn(
        "group/composer relative isolate flex flex-col",
        s.root,
        disabled ? "cursor-not-allowed opacity-50" : "cursor-text",
        className,
      )}
      // Clicks on the padding and the row focus the textarea; controls handle their own.
      onMouseDown={(event) => {
        onMouseDown?.(event);
        const target = event.target as HTMLElement;
        if (disabled || target.closest("button, a, input, textarea, [role='menu']")) return;
        event.preventDefault();
        textareaRef.current?.focus();
      }}
      onDragOver={(event) => {
        if (!onAddFiles || disabled || !hasFiles(event)) return;
        event.preventDefault();
        setDragActive(true);
      }}
      onDragLeave={(event) => {
        // Crossing a child fires leave too; only a real exit clears.
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        setDragActive(false);
      }}
      onDrop={(event) => {
        if (!onAddFiles || disabled || !hasFiles(event)) return;
        event.preventDefault();
        setDragActive(false);
        onAddFiles(Array.from(event.dataTransfer.files));
      }}
      {...props}
    >
      {attachments.length > 0 && (
        <ChipGroup aria-label={labels.attachments}>
          {attachments.map((a) => (
            <Chip
              key={a.id}
              label={
                a.status === "uploading"
                  ? `${a.name} · ${labels.uploading(a.progress ?? 0)}`
                  : a.status === "failed"
                    ? `${a.name} · ${labels.failed}`
                    : a.name
              }
              icon={a.status === "failed" ? CircleAlert : a.kind === "image" ? ImageIcon : FileText}
              numeric={a.status === "uploading"}
              // The status word must stay readable: the chip may use the whole row before it truncates.
              className="max-w-full"
              removeLabel={labels.remove(a.name)}
              onRemove={
                onRemoveAttachment &&
                (() => {
                  if (attachments.length === 1) focusAfterLastRemove.current = true;
                  onRemoveAttachment(a.id);
                })
              }
            />
          ))}
        </ChipGroup>
      )}

      <Textarea
        ref={setTextarea}
        autosize
        rows={1}
        maxRows={s.maxRows}
        value={value}
        maxLength={maxLength}
        disabled={disabled}
        aria-label={labels.input}
        aria-describedby={describedBy}
        placeholder={labels.placeholder}
        // The composer is the frame: the textarea drops its own border and ring and becomes the peer.
        className="peer rounded-none border-0 p-0 opacity-100 hover:border-0 focus-visible:outline-hidden data-focus:outline-hidden"
        onChange={(event) => onValueChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter" || event.shiftKey) return;
          if (event.nativeEvent.isComposing || event.keyCode === 229) return;
          event.preventDefault();
          submit();
        }}
        onPaste={(event) => {
          if (!onAddFiles || disabled) return;
          const files = Array.from(event.clipboardData.files);
          if (files.length === 0) return;
          event.preventDefault();
          onAddFiles(files);
        }}
      />

      <div className="flex min-w-0 items-center gap-2">
        {onAddFiles && (
          <>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept={accept}
              hidden
              tabIndex={-1}
              onChange={(event) => {
                const files = Array.from(event.target.files ?? []);
                if (files.length > 0) onAddFiles(files);
                // Reset so picking the same file again still fires.
                event.target.value = "";
              }}
            />
            <IconButton
              icon={Paperclip}
              label={labels.attach}
              size={s.button}
              disabled={disabled}
              onClick={() => fileRef.current?.click()}
            />
          </>
        )}
        <span
          id={describedBy}
          className="flex min-w-0 flex-1 justify-end gap-1 font-sans text-caption text-ink-muted tabular-nums"
        >
          {dragActive ? (
            <span className="truncate">{labels.dropHint}</span>
          ) : (
            <>
              {/* The hint may truncate in a narrow panel; the counter never does. */}
              {hint != null && <span className="min-w-0 truncate">{hint}</span>}
              {/* Spaces between the parts: not rendered in the flex row, but they keep the description readable. */}
              {hint != null && maxLength != null && <> <span>·</span> </>}
              {maxLength != null && (
                <span className="shrink-0">
                  {labels.counter?.(value.length, maxLength) ?? `${value.length} / ${maxLength}`}
                </span>
              )}
            </>
          )}
        </span>
        {toolbar}
        {showStop ? (
          <IconButton
            ref={stopRef}
            icon={CircleStop}
            label={labels.stop}
            variant="outline"
            size={s.button}
            onFocus={() => (focusedAction.current = "stop")}
            onBlur={() => (focusedAction.current = null)}
            onClick={() => {
              focusedAction.current = "stop";
              onStop?.();
            }}
          />
        ) : (
          <IconButton
            ref={sendRef}
            icon={ArrowUp}
            label={labels.send}
            variant="default"
            size={s.button}
            disabled={!canSend && !sending && !generating}
            loading={sending || generating}
            onFocus={() => (focusedAction.current = "send")}
            onBlur={() => (focusedAction.current = null)}
            onClick={() => {
              focusedAction.current = "send";
              submit();
            }}
          />
        )}
      </div>

      <span
        aria-hidden="true"
        data-slot="chat-composer-frame"
        className={cn(
          "pointer-events-none absolute inset-0 -z-10 rounded-md border transition-colors",
          dragActive
            ? "border-primary-accent bg-primary-subtle"
            : cn(
                "border-input",
                !disabled && "group-hover/composer:border-ink-muted",
                FOCUS_RING_FRAME_PEER,
              ),
        )}
      />
    </div>
  );
});
